import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { getUrl } from '../../_shared/utils';
import { JWTTokenService } from '../jwtToken.service';
import { BaseService } from '../../_shared/base.service';
import { FundraisingService } from './fundraising.service';
import { PledgePerWin } from '../../_models/fundraising/ppw/PledgePerWin';
import { PledgePerWinTeam } from '../../_models/fundraising/ppw/PledgePerWinTeam'; 
import { Game } from '../../_models/fundraising/ppw/Game';
import { Subscriber } from '../../_models/fundraising/ppw/Subscriber';
import { PledgePerWinSubscriber } from '../../_models/fundraising/ppw/PledgePerWinSubscriber';
import { Payout } from '../../_models/fundraising/Payout';

@Injectable()
export class PledgePerWinService extends FundraisingService {

    constructor(
        protected http: HttpClient,
        jwtTokenService: JWTTokenService) { 
          super('cue5050', http, jwtTokenService);
    }

    public completeEvent(id: number): Observable<any> {
        this.InvalidateCache(/^fundraising*/);
        return this.http.get(getUrl(`fundraising/ppw/complete/${id}`), this.requestOptionsAuthenticated('portal'));
    }

    public getById(id: number): Observable<PledgePerWin> {
        return this.CachedHttpGet<PledgePerWin>(`fundraising/ppw/event/${id}`);
    }

    public create(data: PledgePerWin): Observable<PledgePerWin[]> {
        this.InvalidateCache(/^fundraising*/);
        return this.http.post<PledgePerWin[]>(getUrl(`fundraising/ppw/create`), data, this.requestOptionsAuthenticated('portal'));
    }
  
    public update(data: PledgePerWin): Observable<PledgePerWin[]> {
        this.InvalidateCache(/^fundraising*/);
        return this.http.post<PledgePerWin[]>(getUrl(`fundraising/ppw/update`), data, this.requestOptionsAuthenticated('portal'));
    }

    public getAllTeams(): Observable<PledgePerWinTeam[]> {
        return this.CachedHttpGet<PledgePerWinTeam[]>(`fundraising/ppw/teams`);
    }

    public getTeamById(id: number): Observable<PledgePerWinTeam> {
        return this.CachedHttpGet<PledgePerWinTeam>(`fundraising/ppw/teams/${id}`);
    }

    public createTeam(data: PledgePerWinTeam): Observable<number> {
        this.InvalidateCache(/^fundraising*/);
        return this.http.post<number>(getUrl(`fundraising/ppw/team/create`), data, this.requestOptionsAuthenticated('portal'));
    }

    public updateTeam(data: PledgePerWinTeam): Observable<any> {
        this.InvalidateCache(/^fundraising*/);
        return this.http.post<any>(getUrl(`fundraising/ppw/team/update`), data, this.requestOptionsAuthenticated('portal'));
    }

    public updateSortOrder(ppwId: number, sortOrder: number): Observable<any> {
        this.InvalidateCache(/^fundraising*/);
        return this.http.post<any>(getUrl(`fundraising/ppw/update/sort-order/${ppwId}`), sortOrder, this.requestOptionsAuthenticated('portal'));
    }

    public getAllGames(): Observable<Game[]> {
        return this.CachedHttpGet<Game[]>(`fundraising/ppw/games`);
    }

    public getGameById(gameId: number): Observable<Game> {
        return this.http.get<Game>(getUrl(`fundraising/ppw/games/${gameId}`), this.requestOptionsAuthenticated('portal'));
    }

    public createCompletedGame(data: Game): Observable<Game> {

        return this.http.post<Game>(getUrl(`fundraising/ppw/games/create`), data, this.requestOptionsAuthenticated('portal'));
    }

    public createIncompleteCharges(gameId: number): Observable<any> {

        return this.http.post<any>(getUrl(`fundraising/ppw/games/${gameId}/create-incomplete-charges`), null, this.requestOptionsAuthenticated('portal'));
    }

    public sendGameOverComms(gameId: number): Observable<any> {

        return this.http.get<any>(getUrl(`fundraising/ppw/games/${gameId}/send-game-over-comms`), this.requestOptionsAuthenticated('portal'));
    }

    public batchCharges(gameId: number): Observable<any> {

        return this.http.post<any>(getUrl(`fundraising/ppw/games/${gameId}/batch-charges`), null, this.requestOptionsAuthenticated('portal'));
    }

    public sendCompletedGameReport(gameId: number): Observable<any> {
        this.InvalidateCache(/^fundraising*/);
        return this.http.get<any>(getUrl(`fundraising/ppw/games/${gameId}/send-completed-game-report`), this.requestOptionsAuthenticated('portal'));
    }

    public initiateChargeProcess(game: Game) : Observable<any> {
        this.InvalidateCache(/^fundraising*/);
        return this.createCompletedGame(game)
            .pipe(
                concatMap(
                    gameView => {

                        return this.createIncompleteCharges(gameView.pledgePerWinGameId)
                            .pipe(
                                concatMap(_ => this.sendGameOverComms(gameView.pledgePerWinGameId))
                            );
                    }
                )
            );
    }

    public getChargeInformation(ppwId: number): Observable<any> {
        return this.http.get<any>(getUrl(`fundraising/ppw/games/charge-info/${ppwId}`), this.requestOptionsAuthenticated('portal'));
    }

    public reverseCharges(gameId: number): Observable<any> {
        this.InvalidateCache(/^fundraising*/);
        return this.http.post<any>(getUrl(`fundraising/ppw/games/reverse-charges`), gameId, this.requestOptionsAuthenticated('portal'));
    }

    public getAllSubscribers(): Observable<Subscriber[]> {
        return this.CachedHttpGet<Subscriber[]>('fundraising/ppw/subscribers');
    }

    public reverseSubscriberCharges(subscriberId: number, subscriptions: PledgePerWinSubscriber[]) : Observable<any> {

        return this.http.post<any>(getUrl(`fundraising/ppw/subscribers/${subscriberId}/reverse-charges`), subscriptions, this.requestOptionsAuthenticated('portal'));
    }

    public cancelSubscriberSubscriptions(subscriberId: number, subscriptions: PledgePerWinSubscriber[]) : Observable<any> {

        this.InvalidateCache(/^fundraising*/);
        return this.http.post<any>(getUrl(`fundraising/ppw/subscribers/${subscriberId}/cancel`), subscriptions, this.requestOptionsAuthenticated('portal'));
    }

    public getAllPayouts() : Observable<Payout[]> {

        return this.http.get<Payout[]>(getUrl("fundraising/ppw/payouts"), this.requestOptionsAuthenticated('portal'));
    }

    public resendGameReportEmail(pledgePerWinGameId: number) : Observable<any> {

        return this.http.get<any>(getUrl(`fundraising/ppw/resend-game-report/${pledgePerWinGameId}`), this.requestOptionsAuthenticated("portal"));
    }

    public getSubscriberReport () : Observable<any> {

        return this.http.get<any>(getUrl('fundraising/ppw/send-subscriber-report'), this.requestOptionsAuthenticated("portal"));
    }
}